import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ChevronDown, ChevronUp, Download, History, PlusCircle, Search, SlidersHorizontal, Users } from 'lucide-react';
import { toast } from 'sonner';
import type { LoyaltyPointsSummaryToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatDate } from '~/lib/format-date';
import { fetchLoyaltyCustomersForExport, useLoyaltyCustomersList } from '~/hooks/use-loyalty-customers';
import { serverMessage } from '~/lib/api/server-error';
import { exportToCsv } from '~/lib/export-csv';
import type { SortDir } from '~/lib/grid/sort';
import { LoyaltyAdjustDialog, type AdjustTarget } from './adjust-dialog';
import { LoyaltyHistoryDialog } from './history-dialog';

const PAGE_SIZES = [25, 50, 100];

/** Grid fields the server filters and sorts by. */
type SortKey = 'cnm' | 'csnm' | 'em' | 'pnt' | 'rpnt' | 'exdt';

/**
 * Customers' loyalty points (Πόντοι πελατών): one row per customer, paged on the server, with the spendable balance,
 * the points reserved by unpaid orders, a per-row points-history dialog and points adjusted by hand (a row's, or any
 * customer's from the toolbar: a customer with no points has no row).
 */
export function LoyaltyCustomersGridView() {
	const { t } = useTranslation();

	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sortKey, setSortKey] = useState<SortKey>('pnt');
	const [sortDir, setSortDir] = useState<SortDir>('desc');
	const [historyFor, setHistoryFor] = useState<LoyaltyPointsSummaryToken | null>(null);
	const [exporting, setExporting] = useState(false);
	// undefined: closed; null: open, the admin picks the customer.
	const [adjustFor, setAdjustFor] = useState<AdjustTarget | null | undefined>(undefined);

	useEffect(() => {
		const handle = setTimeout(() => {
			setSearch(searchInput.trim());
			setPage(0);
		}, 350);
		return () => clearTimeout(handle);
	}, [searchInput]);

	const { data, isLoading, isError, refetch, isFetching } = useLoyaltyCustomersList({
		skip: page * pageSize,
		take: pageSize,
		search,
		sortSelector: sortKey,
		sortDir
	});

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const fullName = (o: LoyaltyPointsSummaryToken) => [o.cnm, o.csnm].filter(Boolean).join(' ').trim();

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
		else {
			setSortKey(key);
			setSortDir(key === 'pnt' || key === 'rpnt' ? 'desc' : 'asc');
		}
		setPage(0);
	};

	// The search's rows (not the page on screen), as the legacy grid's Excel export gave; a cut export says so.
	const exportCsv = async () => {
		setExporting(true);
		try {
			const { items, totalCount } = await fetchLoyaltyCustomersForExport({ search, sortSelector: sortKey, sortDir });
			exportToCsv(
				'loyalty-customers',
				[
					{ header: t('loyalty.customers.columns.name'), value: (o: LoyaltyPointsSummaryToken) => o.cnm },
					{ header: t('loyalty.customers.columns.surname'), value: (o: LoyaltyPointsSummaryToken) => o.csnm },
					{ header: t('loyalty.customers.columns.points'), value: (o: LoyaltyPointsSummaryToken) => o.pnt ?? 0 },
					{ header: t('loyalty.customers.columns.reserved'), value: (o: LoyaltyPointsSummaryToken) => o.rpnt ?? 0 },
					{ header: t('loyalty.customers.columns.email'), value: (o: LoyaltyPointsSummaryToken) => o.em },
					{ header: t('loyalty.customers.columns.mobile'), value: (o: LoyaltyPointsSummaryToken) => o.mb },
					{ header: t('loyalty.customers.columns.expiration'), value: (o: LoyaltyPointsSummaryToken) => formatDate(o.exdt) }
				],
				items
			);
			if (totalCount > items.length) toast.warning(t('loyalty.customers.exportTruncated', { exported: items.length, total: totalCount }));
		} catch (err) {
			toast.error(serverMessage(err));
		} finally {
			setExporting(false);
		}
	};

	const SortHeader = ({ col, label, className }: { col: SortKey; label: string; className?: string }) => (
		<button type="button" onClick={() => toggleSort(col)} className={cn('inline-flex items-center gap-1 font-medium', className)}>
			{label}
			{sortKey === col && (sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
		</button>
	);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('loyalty.customers.title')}</h1>
					<p className="mt-1 text-sm text-foreground-muted">{t('loyalty.customers.subtitle', { count: total })}</p>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						placeholder={t('loyalty.customers.searchPlaceholder')}
						className="pl-8"
					/>
				</div>
				<Button variant="outline" size="sm" className="ml-auto" onClick={() => setAdjustFor(null)}>
					<PlusCircle size={14} /> {t('loyalty.customers.adjust.anyCustomer')}
				</Button>
				<Button variant="outline" size="sm" onClick={() => void exportCsv()} disabled={exporting || total === 0}>
					<Download size={14} /> {t('loyalty.customers.export')}
				</Button>
			</div>

			<div className="overflow-x-auto rounded-md border border-border bg-surface">
				{isError ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<AlertCircle size={40} className="text-danger-fg" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('loyalty.loadError')}</p>
						<Button variant="outline" onClick={() => void refetch()}>
							{t('common.retry')}
						</Button>
					</div>
				) : isLoading ? (
					<div className="flex flex-col gap-2 p-3">
						{Array.from({ length: 10 }).map((_, i) => (
							<Skeleton key={i} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : rows.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-16 text-center">
						<Users size={36} className="text-foreground-disabled" strokeWidth={1.25} />
						<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
					</div>
				) : (
					<table className={cn('w-full border-collapse text-sm', isFetching && 'opacity-60')}>
						<thead className="bg-elevated text-xs text-foreground-muted">
							<tr className="border-b border-border">
								<th className="px-3 py-2 text-left">
									<SortHeader col="cnm" label={t('loyalty.customers.columns.name')} />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="csnm" label={t('loyalty.customers.columns.surname')} />
								</th>
								<th className="px-3 py-2 text-right">
									<SortHeader col="pnt" label={t('loyalty.customers.columns.points')} className="justify-end" />
								</th>
								<th className="px-3 py-2 text-right">
									<SortHeader col="rpnt" label={t('loyalty.customers.columns.reserved')} className="justify-end" />
								</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="em" label={t('loyalty.customers.columns.email')} />
								</th>
								<th className="px-3 py-2 text-left font-medium">{t('loyalty.customers.columns.mobile')}</th>
								<th className="px-3 py-2 text-left">
									<SortHeader col="exdt" label={t('loyalty.customers.columns.expiration')} />
								</th>
								<th className="px-3 py-2" />
							</tr>
						</thead>
						<tbody>
							{rows.map(o => (
								<tr key={o.cid} className="border-b border-border/60 hover:bg-muted/50">
									<td className="px-3 py-2 font-medium text-foreground">{o.cnm || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{o.csnm || '—'}</td>
									<td className="px-3 py-2 text-right font-medium tabular-nums text-foreground">{o.pnt ?? 0}</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground-muted">{o.rpnt ?? 0}</td>
									<td className="px-3 py-2 text-foreground-muted">{o.em || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{o.mb || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDate(o.exdt)}</td>
									<td className="px-3 py-2 text-right whitespace-nowrap">
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											aria-label={t('loyalty.customers.adjust.action')}
											title={t('loyalty.customers.adjust.action')}
											onClick={() => setAdjustFor({ cid: o.cid ?? 0, name: fullName(o) || (o.em ?? ''), points: o.pnt ?? 0 })}>
											<SlidersHorizontal size={15} />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											className="h-7 w-7"
											aria-label={t('loyalty.customers.history.action')}
											title={t('loyalty.customers.history.action')}
											onClick={() => setHistoryFor(o)}>
											<History size={15} />
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<select
						value={pageSize}
						onChange={e => {
							setPageSize(Number(e.target.value));
							setPage(0);
						}}
						className="h-8 rounded-md border border-border bg-surface px-2">
						{PAGE_SIZES.map(s => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>
				<div className="flex items-center gap-2">
					<span>{t('loyalty.products.pageInfo', { page: page + 1, pages: pageCount, total })}</span>
					<Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
						{t('common.previous')}
					</Button>
					<Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
						{t('common.next')}
					</Button>
				</div>
			</div>

			<LoyaltyAdjustDialog
				open={adjustFor !== undefined}
				onOpenChange={open => !open && setAdjustFor(undefined)}
				target={adjustFor ?? null}
			/>

			<LoyaltyHistoryDialog
				open={Boolean(historyFor)}
				onOpenChange={open => !open && setHistoryFor(null)}
				customerId={historyFor?.cid ?? null}
				customerName={historyFor ? fullName(historyFor) : undefined}
			/>
		</div>
	);
}
